import { VideoItem } from "../types/video.js";

export const getApiBaseUrl = (): string => {
  const envApiUrl = ((import.meta as any).env?.VITE_API_URL as string | undefined)?.trim().replace(/\/$/, "");
  if (envApiUrl) return envApiUrl;
  if (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
    return "https://hp-helix-api.onrender.com";
  }
  return ""; // Relative proxy in local Vite dev
};

export interface FetchVideosParams {
  channelId?: string;
  channel_id?: string;
  visibility?: string;
  paywalled?: boolean;
  is_paywalled?: boolean;
  limit?: number;
}

/**
 * Fetch videos from Fastify REST API (/api/v1/videos)
 */
export async function fetchApiVideos(params?: FetchVideosParams): Promise<VideoItem[]> {
  const apiBase = getApiBaseUrl();
  const query = new URLSearchParams();

  if (params?.channelId || params?.channel_id) {
    query.set("channel_id", params.channelId || params.channel_id || "");
  }
  if (params?.visibility) {
    query.set("visibility", params.visibility);
  }
  if (params?.paywalled !== undefined || params?.is_paywalled !== undefined) {
    query.set("paywalled", String(params?.paywalled ?? params?.is_paywalled));
  }
  if (params?.limit) {
    query.set("limit", String(params.limit));
  }

  const endpoint = `${apiBase}/api/v1/videos${query.toString() ? `?${query.toString()}` : ""}`;

  try {
    const res = await fetch(endpoint, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      throw new Error(`API returned ${res.status}`);
    }
    const body = (await res.json()) as { success: boolean; data: VideoItem[] };
    return body.data || [];
  } catch (err) {
    console.warn("[api] fetchApiVideos notice:", err);
    return [];
  }
}

/**
 * Fetch single video from Fastify REST API (/api/v1/videos/:id)
 */
export async function fetchApiVideoById(id: string): Promise<VideoItem | null> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/videos/${id}`;

  try {
    const res = await fetch(endpoint, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { success: boolean; data: VideoItem };
    return body.data || null;
  } catch (err) {
    console.warn("[api] fetchApiVideoById notice:", err);
    return null;
  }
}

/**
 * Patch video metadata via Fastify REST API (/api/v1/videos/:id)
 */
export async function patchApiVideo(id: string, updates: Partial<VideoItem> & { [key: string]: any }): Promise<boolean> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/videos/${id}`;

  try {
    const res = await fetch(endpoint, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    return res.ok;
  } catch (err) {
    console.warn("[api] patchApiVideo notice:", err);
    return false;
  }
}

/**
 * Unlock paywalled video via Fastify REST API (/api/v1/videos/:id/unlock)
 */
export async function unlockApiVideo(videoId: string, payload: {
  userId?: string;
  channelId?: string;
  amount?: number;
  currency?: string;
  paymentMethod?: string;
  phone?: string;
}): Promise<{ success: boolean; isUnlocked: boolean; data?: any }> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/videos/${videoId}/unlock`;

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return { success: false, isUnlocked: false };
    return await res.json();
  } catch (err) {
    console.warn("[api] unlockApiVideo notice:", err);
    return { success: false, isUnlocked: false };
  }
}

/**
 * Fetch creator payout withdrawal requests (/api/v1/monetization/payout-requests)
 */
export async function fetchApiPayoutRequests(channelId: string): Promise<any[]> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/monetization/payout-requests?channelId=${encodeURIComponent(channelId)}`;

  try {
    const res = await fetch(endpoint);
    if (!res.ok) return [];
    const body = await res.json();
    return body.data || [];
  } catch {
    return [];
  }
}

/**
 * Submit creator payout withdrawal request (/api/v1/monetization/payout-requests)
 */
export async function submitApiPayoutRequest(payload: {
  channelId: string;
  amount: number;
  currency: string;
  payoutMethod: string;
  phoneNumber: string;
  accountName?: string;
  notes?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/monetization/payout-requests`;

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || "Network request failed" };
  }
}

/**
 * Fetch creator tips (/api/v1/monetization/tips)
 */
export async function fetchApiTips(channelId: string): Promise<any[]> {
  const apiBase = getApiBaseUrl();
  const endpoint = `${apiBase}/api/v1/monetization/tips?channelId=${encodeURIComponent(channelId)}`;

  try {
    const res = await fetch(endpoint);
    if (!res.ok) return [];
    const body = await res.json();
    return body.data || [];
  } catch {
    return [];
  }
}
